import { createContext, useContext, useReducer, useCallback, ReactNode } from 'react'
import {
  DreamityMainCategoryResDto,
  DreamityMainCategorySaveReqDto,
  DreamityMainCategoryModifyReqDto,
  DreamitySubCategoryResDto,
  DreamitySubCategorySaveReqDto,
  DreamitySubCategoryModifyReqDto,
} from '../types/category.types'
import { getStaticMainCategories, getStaticSubCategories } from '@/constants/localPlannerCategories'

interface CategoryState {
  mainCategories: DreamityMainCategoryResDto[]
  subCategories: DreamitySubCategoryResDto[]
  selectedMainCategory: DreamityMainCategoryResDto | null
  selectedSubCategory: DreamitySubCategoryResDto | null
  loading: boolean
  error: string | null
  language: string
}

type CategoryAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_MAIN_CATEGORIES'; payload: DreamityMainCategoryResDto[] }
  | { type: 'SET_SUB_CATEGORIES'; payload: DreamitySubCategoryResDto[] }
  | { type: 'SET_SELECTED_MAIN_CATEGORY'; payload: DreamityMainCategoryResDto | null }
  | { type: 'SET_SELECTED_SUB_CATEGORY'; payload: DreamitySubCategoryResDto | null }
  | { type: 'UPDATE_MAIN_CATEGORY_IN_LIST'; payload: DreamityMainCategoryResDto }
  | { type: 'UPDATE_SUB_CATEGORY_IN_LIST'; payload: DreamitySubCategoryResDto }
  | { type: 'DELETE_MAIN_CATEGORY'; payload: number }
  | { type: 'DELETE_SUB_CATEGORY'; payload: number }
  | { type: 'SET_LANGUAGE'; payload: string }

interface CategoryContextType extends CategoryState {
  // 대분류 조회
  fetchMainCategories: (language?: string) => Promise<void>
  // 대분류 생성
  createMainCategory: (category: DreamityMainCategorySaveReqDto) => Promise<void>
  // 대분류 수정
  updateMainCategory: (id: number, category: DreamityMainCategoryModifyReqDto) => Promise<void>
  // 대분류 삭제
  deleteMainCategory: (id: number) => Promise<void>
  // 소분류 조회
  fetchSubCategories: (language?: string) => Promise<void>
  // 소분류 생성
  createSubCategory: (category: DreamitySubCategorySaveReqDto) => Promise<void>
  // 소분류 수정
  updateSubCategory: (id: number, category: DreamitySubCategoryModifyReqDto) => Promise<void>
  // 소분류 삭제
  deleteSubCategory: (id: number) => Promise<void>
  // 대분류 선택
  selectMainCategory: (category: DreamityMainCategoryResDto | null) => void
  // 소분류 선택
  selectSubCategory: (category: DreamitySubCategoryResDto | null) => void
}

const CategoryContext = createContext<CategoryContextType | undefined>(undefined)

const initialState: CategoryState = {
  mainCategories: [],
  subCategories: [],
  selectedMainCategory: null,
  selectedSubCategory: null,
  loading: false,
  error: null,
  language: 'ko',
}

function categoryReducer(
  state: CategoryState,
  action: CategoryAction
): CategoryState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload }
    case 'SET_MAIN_CATEGORIES':
      return { ...state, mainCategories: action.payload }
    case 'SET_SUB_CATEGORIES':
      return { ...state, subCategories: action.payload }
    case 'SET_SELECTED_MAIN_CATEGORY':
      return { ...state, selectedMainCategory: action.payload }
    case 'SET_SELECTED_SUB_CATEGORY':
      return { ...state, selectedSubCategory: action.payload }
    case 'UPDATE_MAIN_CATEGORY_IN_LIST':
      return {
        ...state,
        mainCategories: state.mainCategories.map((category) =>
          category.id === action.payload.id ? action.payload : category
        ),
        selectedMainCategory:
          state.selectedMainCategory?.id === action.payload.id
            ? action.payload
            : state.selectedMainCategory,
      }
    case 'UPDATE_SUB_CATEGORY_IN_LIST':
      return {
        ...state,
        subCategories: state.subCategories.map((category) =>
          category.id === action.payload.id ? action.payload : category
        ),
        selectedSubCategory:
          state.selectedSubCategory?.id === action.payload.id
            ? action.payload
            : state.selectedSubCategory,
      }
    case 'DELETE_MAIN_CATEGORY':
      return {
        ...state,
        mainCategories: state.mainCategories.filter(
          (category) => category.id !== action.payload
        ),
        selectedMainCategory:
          state.selectedMainCategory?.id === action.payload
            ? null
            : state.selectedMainCategory,
      }
    case 'DELETE_SUB_CATEGORY':
      return {
        ...state,
        subCategories: state.subCategories.filter(
          (category) => category.id !== action.payload
        ),
        selectedSubCategory:
          state.selectedSubCategory?.id === action.payload
            ? null
            : state.selectedSubCategory,
      }
    case 'SET_LANGUAGE':
      return { ...state, language: action.payload }
    default:
      return state
  }
}

interface CategoryProviderProps {
  children: ReactNode
}

export function CategoryProvider({ children }: CategoryProviderProps) {
  const [state, dispatch] = useReducer(categoryReducer, initialState)

  /**
   * 1. 대분류(Main Category) 조회
   * 대분류 전체 조회 (언어 선택 가능)
   */
  const fetchMainCategories = useCallback(async (language: string = 'ko') => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      const categories = getStaticMainCategories(language)
      dispatch({ type: 'SET_MAIN_CATEGORIES', payload: categories })
      dispatch({ type: 'SET_LANGUAGE', payload: language })
      dispatch({ type: 'SET_LOADING', payload: false })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [])

  /**
   * 2. 대분류(Main Category) 생성
   */
  const createMainCategory = async (_category: DreamityMainCategorySaveReqDto) => {
    console.warn('[Category] 로컬 전용 앱: 카테고리 생성은 지원하지 않습니다.')
  }

  /**
   * 3. 대분류(Main Category) 수정
   */
  const updateMainCategory = async (_id: number, _category: DreamityMainCategoryModifyReqDto) => {
    console.warn('[Category] 로컬 전용 앱: 카테고리 수정은 지원하지 않습니다.')
  }

  /**
   * 4. 대분류(Main Category) 삭제
   */
  const deleteMainCategory = async (_id: number) => {
    console.warn('[Category] 로컬 전용 앱: 카테고리 삭제는 지원하지 않습니다.')
  }

  /**
   * 5. 소분류(Sub Category) 조회
   * 소분류 전체 조회 (언어 선택 가능)
   */
  const fetchSubCategories = useCallback(async (language: string = 'ko') => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      const categories = getStaticSubCategories(language)
      dispatch({ type: 'SET_SUB_CATEGORIES', payload: categories })
      dispatch({ type: 'SET_LANGUAGE', payload: language })
      dispatch({ type: 'SET_LOADING', payload: false })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [])

  /**
   * 6. 소분류(Sub Category) 생성
   */
  const createSubCategory = async (_category: DreamitySubCategorySaveReqDto) => {
    console.warn('[Category] 로컬 전용 앱: 소분류 생성은 지원하지 않습니다.')
  }

  /**
   * 7. 소분류(Sub Category) 수정
   */
  const updateSubCategory = async (_id: number, _category: DreamitySubCategoryModifyReqDto) => {
    console.warn('[Category] 로컬 전용 앱: 소분류 수정은 지원하지 않습니다.')
  }

  /**
   * 8. 소분류(Sub Category) 삭제
   */
  const deleteSubCategory = async (_id: number) => {
    console.warn('[Category] 로컬 전용 앱: 소분류 삭제는 지원하지 않습니다.')
  }

  /**
   * 대분류 선택
   */
  const selectMainCategory = (category: DreamityMainCategoryResDto | null) => {
    dispatch({ type: 'SET_SELECTED_MAIN_CATEGORY', payload: category })
  }

  /**
   * 소분류 선택
   */
  const selectSubCategory = (category: DreamitySubCategoryResDto | null) => {
    dispatch({ type: 'SET_SELECTED_SUB_CATEGORY', payload: category })
  }

  return (
    <CategoryContext.Provider
      value={{
        ...state,
        fetchMainCategories,
        createMainCategory,
        updateMainCategory,
        deleteMainCategory,
        fetchSubCategories,
        createSubCategory,
        updateSubCategory,
        deleteSubCategory,
        selectMainCategory,
        selectSubCategory,
      }}
    >
      {children}
    </CategoryContext.Provider>
  )
}

// useCategory hook은 hooks/useCategory.ts에서 export합니다
export function useCategoryContext() {
  const context = useContext(CategoryContext)
  if (context === undefined) {
    throw new Error('useCategory must be used within a CategoryProvider')
  }
  return context
}

